// Registro de documentos legales de MYKLUBB (fuente única, versionada).
//
// El contenido es MARKDOWN con placeholders entre corchetes (ej. [NOMBRE DEL CLUB])
// que se rellenan en tiempo de render con los datos de la organización / plataforma
// (ver renderLegalDoc en src/legal/render.js).
//
// Cada documento tiene UNA version vigente (`vigente: true`); las anteriores se quedan en el
// registro como historico para el audit trail. Para cambiar un texto se agrega una entrada
// nueva con `version` distinta y `vigente: true`, y la anterior pasa a `vigente: false`.
//
// Subir de version no es gratis: `consentimientos.documento_version` guarda cual firmo cada
// tutor, y un cambio SUSTANCIAL --uno que amplie lo que se recaba, para que se usa o a quien
// se transfiere-- obliga a volver a pedir la firma. Los cambios de forma (redaccion,
// aclaraciones que no amplian el uso) no.
//
// 1.1 (pieza 2b, spec 2026-10-11 §7): el Aviso del club, el DPA, la carta del tutor y la
// declaracion del club sin MYKLUBB declaran siempre lo que se comparte con la organizadora de
// un torneo. Los revisa Macrolex antes de PROD.
//
// 1.1 corregida el 2026-10-15 (credenciales del rival, spec 2026-10-15): el club rival de cada
// partido ve la foto de los jugadores y del cuerpo tecnico, solo alrededor del partido. Se corrigio
// sin cambiar de version: la 1.1 no estaba en PROD y ningun tutor real la habia firmado.

export const LEGAL_DOC_KEYS = {
  DECLARACION_CLUB_SIN_MYKLUBB: 'declaracion_club_sin_myklubb',
  AVISO_CLUB: 'aviso_club',
  AVISO_MYKLUBB: 'aviso_myklubb',
  DPA: 'dpa',
  CARTA_CONSENTIMIENTO: 'carta_consentimiento',
  TOS_SAAS: 'tos_saas',
}

// Documentos que el club (su admin) debe aceptar para ACTIVAR la organización
// (puerta legal en el primer acceso).
export const REQUIRED_ORG_LEGAL_DOCS = [
  LEGAL_DOC_KEYS.DPA,
  LEGAL_DOC_KEYS.TOS_SAAS,
]

// Datos de MYKLUBB sin los cuales SUS documentos (aviso de la plataforma, DPA y
// Contrato de Servicio) salen con "[por definir]". Es el espejo, un piso arriba, de
// `camposLegalesFaltantes` en lib/consent.js: igual que un club no puede pedir
// firmas con su Aviso incompleto, la plataforma no puede pedirle a un club que
// acepte un contrato que no dice quién es la contraparte ni ante qué tribunales.
//
// El RFC NO está aquí a propósito: es opcional y el contrato lo omite si falta.
export const CAMPOS_LEGALES_PLATAFORMA = [
  ['razon_social', 'razón social'],
  ['domicilio', 'domicilio'],
  ['correo_privacidad', 'correo ARCO'],
  ['correo_contacto', 'correo de contacto'],
  ['ciudad_jurisdiccion', 'ciudad para jurisdicción'],
]

export const camposLegalesPlataformaFaltantes = (legal = {}) =>
  CAMPOS_LEGALES_PLATAFORMA
    .filter(([campo]) => !String(legal?.[campo] || '').trim())
    .map(([, etiqueta]) => etiqueta)

export const entidadLegalPlataformaCompleta = (legal = {}) =>
  camposLegalesPlataformaFaltantes(legal).length === 0

// Placeholders que rellena cada documento (para validación/documentación).
export const LEGAL_PLACEHOLDERS = {
  aviso_club: ['[NOMBRE DEL CLUB]', '[RAZÓN SOCIAL]', '[DOMICILIO]', '[CORREO ARCO]', '[FECHA]'],
  aviso_myklubb: ['[RAZÓN SOCIAL DE MYKLUBB]', '[DOMICILIO]', '[CORREO ARCO]', '[FECHA]'],
  dpa: ['[RAZÓN SOCIAL DE MYKLUBB]', '[FECHA/VERSIÓN]'],
  carta_consentimiento: ['[NOMBRE DEL CLUB]'],
  tos_saas: ['[RAZÓN SOCIAL DE MYKLUBB]', '[DOMICILIO]', '[RFC PROVEEDOR]', '[CORREO DE CONTACTO]', '[CIUDAD/ESTADO PARA JURISDICCIÓN]', '[FECHA/VERSIÓN]'],
}

const AVISO_CLUB_1_0 = `# Aviso de Privacidad Integral — [NOMBRE DEL CLUB]

## 1. Identidad y domicilio del responsable

[RAZÓN SOCIAL] ("el Club"), con domicilio en [DOMICILIO], es el responsable del tratamiento de los datos personales de los jugadores que inscribe, conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP).

El Club utiliza la plataforma **MYKLUBB** como encargado del tratamiento. MYKLUBB trata los datos únicamente por cuenta y bajo instrucciones del Club.

## 2. Datos personales que se recaban

**Identificación del jugador:** nombre completo, fecha de nacimiento, género, CURP, apodo o dorsal.

**Contacto de emergencia:** nombre y teléfono del contacto o tutor.

**Responsable de pago:** se captura únicamente cuando difiere del tutor; registro de cuotas y pagos.

**Teléfono del jugador:** solo para jugadores mayores de edad; es de uso interno y no se comparte con terceros.

**Documentos:** fotografía del jugador, carta responsiva y reglamento firmado.

> El Club aplica el principio de minimización: recaba únicamente los datos necesarios para la actividad deportiva. No se recaban datos de salud en la plataforma.

## 3. Finalidades del tratamiento

**Primarias** (necesarias): inscripción y gestión de la membresía; control de asistencia y conformación de equipos; participación en entrenamientos, partidos, ligas y torneos; gestión de cuotas y pagos; cumplimiento de reglamentos aplicables.

**Secundarias** (el titular puede oponerse): comunicaciones informativas del Club y, en su caso, uso de imagen conforme a la sección 5.

> Para oponerse a finalidades secundarias, enviar un correo a [CORREO ARCO]. Negarse no afecta la membresía del jugador.

## 4. Tratamiento de datos de menores de edad

La mayoría de los jugadores son menores. En todo tratamiento prevalece el **interés superior de la niña, niño y adolescente**. El registro y la autorización los otorga el padre, madre o tutor legal, quien declara contar con la representación legal del menor. Cuando el jugador es **mayor de edad, otorga el consentimiento por sí mismo**.

## 5. Uso de imagen (fotografía)

La fotografía se utiliza para la identificación del jugador dentro del Club y la plataforma. Cualquier uso para difusión (redes sociales, materiales promocionales) requiere **autorización expresa y por separado** del tutor (o del jugador mayor de edad), revocable en cualquier momento.

## 6. Transferencias, remisiones y almacenamiento

**Encargado (plataforma):** MYKLUBB trata los datos por cuenta del Club. No los usa para fines propios; su personal de soporte accede solo cuando es necesario para resolver incidencias, dejando registro de cada acceso.

**Almacenamiento en la nube (sub-encargado):** la información se almacena mediante Supabase, que puede alojar los datos en servidores **fuera de México**. Los datos se mantienen cifrados en reposo y en tránsito, bajo obligaciones contractuales de confidencialidad y seguridad.

**Identidad compartida entre clubes:** cuando un jugador pertenece a más de un club, ciertos datos de identidad (CURP) pueden mantenerse de forma unificada para evitar duplicidad. Cada club accede únicamente a los datos de los jugadores que él inscribe.

**Organizadores de torneos y ligas:** al inscribir al jugador en un torneo o liga, el Club comparte con el organizador únicamente los datos mínimos necesarios (**nombre, categoría y elegibilidad**).[TRANSFERENCIA CURP][TRANSFERENCIA FOTO] La fecha de nacimiento **no se comparte**: la edad se verifica dentro de la plataforma y al organizador solo se le informa si el jugador cumple con la categoría, sin revelar su edad ni su año de nacimiento. El organizador se convierte en responsable de los datos que recibe. **No se comparten datos de contacto, de pago ni documentos.**

> Salvo lo anterior y las excepciones de la LFPDPPP, no se transfieren datos a terceros sin consentimiento.

## 7. Conservación de datos

Los datos y documentos se conservan mientras dure la membresía del jugador y se eliminan o archivan una vez que el jugador se da de baja o permanece inactivo por el periodo definido por el Club.

## 8. Medios para ejercer los derechos ARCO

El titular o su representante legal puede ejercer en cualquier momento sus derechos de **Acceso, Rectificación, Cancelación y Oposición (ARCO)** y revocar el consentimiento, enviando solicitud a [CORREO ARCO] con: nombre del titular y solicitante, documento que acredite identidad y, en su caso, representación legal del menor, descripción del derecho que desea ejercer y un medio para recibir respuesta. El ejercicio es gratuito.

## 9. Cookies

La plataforma puede emplear cookies para el funcionamiento del servicio. El usuario puede configurar su navegador para limitarlas.

## 10. Cambios al aviso

Este aviso puede modificarse por cambios legales u operativos; las modificaciones se comunicarán a través de la plataforma o por los medios de contacto registrados.

## 11. Autoridad

El titular puede acudir ante la autoridad competente en materia de protección de datos personales en posesión de particulares en México.

---

**Última actualización:** [FECHA]
`

const AVISO_MYKLUBB = `# Aviso de Privacidad — MYKLUBB

> Aviso de la **plataforma MYKLUBB** dirigido a los administradores y titulares de cuenta de los clubes. Aquí MYKLUBB es el **responsable** de los datos de la cuenta (distinto del aviso del club, donde MYKLUBB es encargado de los datos de los jugadores).

## 1. Responsable

[RAZÓN SOCIAL DE MYKLUBB] ("MYKLUBB"), con domicilio en [DOMICILIO], es responsable del tratamiento de los datos personales de los administradores y titulares de cuenta que usan la plataforma, conforme a la LFPDPPP.

## 2. Datos que se recaban

- **Identificación y contacto:** nombre, correo electrónico, teléfono.
- **Datos de cuenta:** credenciales de acceso, rol dentro del club, registros de actividad y uso.
- **Datos de facturación:** los necesarios para procesar pagos de la suscripción al servicio.

> MYKLUBB **no es responsable** de los datos de los jugadores; esos pertenecen a cada club. Respecto de ellos, MYKLUBB actúa como encargado conforme al convenio de tratamiento (DPA).

## 2 bis. Registro de identidad deportiva del atleta

MYKLUBB mantiene, por decisión propia y para uso interno del sistema, un **registro de identidad unificado por CURP** que le permite reconocer a un mismo atleta a lo largo de su trayectoria deportiva, aunque participe en distintos clubes, ligas o regiones. La finalidad es evitar registros duplicados, dar seguimiento al desarrollo deportivo del atleta en el tiempo y, eventualmente, permitir que el propio atleta (o su tutor) acceda a su historial deportivo.

Respecto de esta capa de identidad, MYKLUBB actúa como **corresponsable**, con base en la autorización expresa que el tutor o el atleta mayor de edad otorga en la carta de consentimiento. La correlación es de uso interno: **ningún club accede a la información de un jugador que corresponda a otro club**, salvo que el propio atleta o su tutor lo autorice.

## 3. Finalidades

**Primarias:** crear y administrar la cuenta; prestar el servicio de la plataforma; soporte técnico; facturación y cobro de la suscripción; seguridad y prevención de fraude; cumplimiento de obligaciones legales.

**Secundarias** (puede oponerse): comunicaciones sobre nuevas funciones y mejoras del producto.

## 4. Transferencias y proveedores

MYKLUBB se apoya en proveedores que actúan como encargados, entre ellos **Supabase** (infraestructura y almacenamiento, que puede alojar datos fuera de México, cifrados en reposo y en tránsito) y el procesador de pagos de la suscripción. Estos proveedores están obligados contractualmente a la confidencialidad y seguridad de la información. No se transfieren datos a terceros para fines propios sin consentimiento, salvo las excepciones de la LFPDPPP.

## 5. Derechos ARCO

El titular puede ejercer sus derechos de Acceso, Rectificación, Cancelación y Oposición, y revocar su consentimiento, enviando solicitud a [CORREO ARCO] con la información que acredite su identidad. El ejercicio es gratuito.

## 6. Conservación

Los datos de la cuenta se conservan mientras la cuenta esté activa y durante el periodo necesario para cumplir obligaciones legales (p. ej. fiscales) tras su cancelación.

## 7. Cookies

La plataforma utiliza cookies para el funcionamiento del servicio y para recordar la sesión. El usuario puede configurarlas en su navegador.

## 8. Cambios y autoridad

Este aviso puede modificarse; los cambios se comunicarán a través de la plataforma. El titular puede acudir ante la autoridad competente en materia de protección de datos personales en posesión de particulares en México.

---

**Última actualización:** [FECHA]
`

const DPA_1_0 = `# Convenio de Tratamiento de Datos Personales (DPA) — MYKLUBB ↔ Club

> Convenio entre **MYKLUBB** (encargado) y el **Club** (responsable). Define cómo MYKLUBB trata, por cuenta del Club, los datos personales de los jugadores.

## 1. Objeto y roles

**Las partes:** [RAZÓN SOCIAL DE MYKLUBB], operador de la plataforma MYKLUBB, y el Club identificado en la cuenta desde la que se acepta este convenio.

El Club es el **responsable** de los datos personales de sus jugadores. MYKLUBB es el **encargado** que los trata exclusivamente por cuenta y bajo instrucciones del Club, para prestar el servicio de gestión deportiva de la plataforma.

## 2. Obligaciones del Club (responsable)

1. **Obtener el consentimiento válido** del tutor (o del jugador mayor de edad) para el tratamiento de los datos de cada jugador, **utilizando el mecanismo que provee la plataforma**, sin sustituirlo ni omitirlo.
2. Poner a disposición de los titulares el **aviso de privacidad** del Club.
3. Garantizar que los datos que captura son exactos y que cuenta con base legítima para tratarlos.
4. Atender, como responsable, las solicitudes de derechos ARCO de sus jugadores (con la asistencia de MYKLUBB prevista en §3).

## 3. Obligaciones de MYKLUBB (encargado)

1. Tratar los datos **únicamente conforme a las instrucciones** del Club y a las finalidades del servicio; no usarlos para fines propios.
2. Guardar **confidencialidad** y exigirla a su personal.
3. Implementar **medidas de seguridad** administrativas, técnicas y físicas (cifrado en reposo y en tránsito, control de acceso, registro de accesos).
4. **Acceso de soporte limitado:** el personal de MYKLUBB accede a los datos solo cuando es necesario para resolver incidencias, dejando **registro de cada acceso**.
5. **Asistir al Club** en la atención de solicitudes ARCO y en notificaciones de incidentes de seguridad.
6. **Devolver o suprimir** los datos al terminar la relación, conforme a la política de retención.
7. No subcontratar fuera de los sub-encargados autorizados (§4) sin informar al Club.

## 4. Sub-encargados autorizados

El Club autoriza a MYKLUBB a apoyarse en los siguientes sub-encargados:

| Sub-encargado | Función | Ubicación de datos |
|---|---|---|
| Supabase | Base de datos, almacenamiento y autenticación | Fuera de México (nube; cifrado en reposo y en tránsito) |

MYKLUBB mantiene con sus sub-encargados obligaciones de confidencialidad y seguridad equivalentes a las de este convenio (p. ej. el DPA firmado con Supabase).

## 5. Transferencia internacional

Al almacenarse los datos con un sub-encargado fuera de México, existe una transferencia internacional bajo la figura de remisión a encargado. MYKLUBB se asegura de que dicho proveedor quede obligado contractualmente a proteger los datos conforme a este convenio.

## 6. Capa de identidad unificada y corresponsabilidad

MYKLUBB mantiene, **por decisión propia y para uso interno del sistema**, un registro de identidad unificado por CURP que le permite reconocer a un mismo atleta a lo largo de su trayectoria deportiva, aunque participe en distintos clubes, ligas o regiones. La finalidad es evitar registros duplicados, dar seguimiento al desarrollo deportivo del atleta en el tiempo y, eventualmente, permitir que el propio atleta (o su tutor) acceda a su historial deportivo.

Respecto de **esta capa de identidad unificada**, y solo de ella, MYKLUBB actúa como **corresponsable** (no como encargado), pues su finalidad es propia de MYKLUBB y no una instrucción del Club. En consecuencia, respecto de esa capa MYKLUBB:

1. Cuenta con **base de licitud propia**: la autorización expresa que el tutor (o el atleta mayor de edad) otorga en la carta de consentimiento.
2. Declara esta finalidad en su **propio aviso de privacidad**.
3. Responde **directamente ante la autoridad** por el tratamiento de esa capa.

La correlación de identidad es de uso interno: **ningún club accede a la información de un jugador que corresponda a otro club**, salvo que el propio atleta o su tutor lo autorice. Respecto del resto de los datos de cada jugador, se mantiene la relación responsable (Club) / encargado (MYKLUBB) del §1.

## 7. Compartición con organizadores de torneos

Cuando el Club inscriba a un jugador en un torneo o liga, la plataforma comparte con el organizador únicamente los datos marcados como compartibles (nombre, categoría, elegibilidad).[TRANSFERENCIA CURP DPA][TRANSFERENCIA FOTO DPA] Los datos de contacto, de pago y los documentos son de uso interno y no se comparten. El organizador, al recibir esos datos, actúa como responsable respecto de ellos.

## 8. Incidentes de seguridad

MYKLUBB notificará al Club sin demora indebida cualquier incidente de seguridad que afecte los datos de los jugadores del Club, con la información disponible para que el Club cumpla sus obligaciones como responsable.

## 9. Vigencia y terminación

Este convenio rige mientras el Club use la plataforma. Al terminar, MYKLUBB devolverá o suprimirá los datos conforme a la política de retención, salvo obligación legal de conservarlos.

## 10. Aceptación

La aceptación electrónica del Club durante el alta (o la firma del documento) constituye su conformidad con este convenio. Se registra fecha, versión y usuario que acepta.

---

**Versión / fecha:** [FECHA/VERSIÓN]
`

// La carta de consentimiento tiene diseño propio (HTML de marca) que se integra en
// Fase 4. Aquí se registra su metadata/versión para el audit trail; el cuerpo real
// se enlaza desde el flujo de consentimiento.
// Texto del paquete revisado (CARTA_CONSENTIMIENTO_TUTOR). Hasta la 1.0 esta
// entrada era un cascaron -- registraba "acepto la version X" de un documento sin
// cuerpo. La pantalla de firma muestra ESTE texto, no una redaccion propia.
const CARTA_CONSENTIMIENTO_1_0 = `# Autorización y consentimiento del padre, madre o tutor — [NOMBRE DEL CLUB]

**Responsable del tratamiento:** [NOMBRE DEL CLUB]

Este documento permite a [NOMBRE DEL CLUB] (en adelante, el Club) recabar la autorización de quien ejerce la patria potestad o tutela del menor para el tratamiento de sus datos personales, conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP) y a la Ley General de los Derechos de Niñas, Niños y Adolescentes (LGDNNA), y al Aviso de Privacidad del Club. En todo tratamiento prevalece el **interés superior** de la niña, niño o adolescente.

## Datos del jugador

Nombre completo · CURP · Fecha de nacimiento · Club y categoría.

## Datos del padre, madre o tutor

Nombre completo · Parentesco · Teléfono · Correo electrónico.

## Tratamiento de datos del menor (obligatorio)

Como padre, madre o tutor del menor identificado en este documento, manifiesto que:

1. Cuento con la **representación legal** del menor.
2. He leído y acepto el **Aviso de Privacidad del Club**.
3. **Autorizo el tratamiento** de los datos personales del menor para su inscripción, identificación, gestión deportiva, participación en entrenamientos, partidos, ligas y torneos, y administración de cuotas.
4. **Autorizo a la plataforma MYKLUBB** a mantener un registro de identidad unificado por CURP, de uso interno, para reconocer al atleta a lo largo de su trayectoria deportiva, evitar registros duplicados y dar seguimiento a su desarrollo. Ningún club accede a la información del menor que corresponda a otro club, salvo que yo lo autorice.

> Acepto y autorizo el tratamiento de los datos del menor conforme a lo anterior.

## Uso de imagen para difusión (opcional)

La fotografía del menor se utiliza para su identificación dentro del Club y la plataforma. De forma adicional, el Club solicita su autorización para usar la imagen del menor en **redes sociales y materiales de difusión**. Esta autorización es **revocable en cualquier momento**.

**Negar esta autorización no afecta la inscripción del menor.**

---

Firma del padre, madre o tutor · Fecha.

*Aviso de Privacidad completo disponible en la propia plataforma.*
`

const TOS_SAAS = `# Términos del Servicio — Contrato de Prestación de Servicios (SaaS) — MYKLUBB

Los presentes Términos del Servicio (el "Contrato") regulan la contratación y uso de la plataforma MYKLUBB. Al darse de alta y aceptar electrónicamente este Contrato, EL CLIENTE manifiesta su conformidad con todas sus cláusulas.

## 1. Definiciones

1.1. **EL PROVEEDOR**: [RAZÓN SOCIAL DE MYKLUBB], con domicilio en [DOMICILIO][RFC PROVEEDOR], licenciante y operador de la plataforma MYKLUBB.

1.2. **EL CLIENTE**: la organización, club, liga, academia o federación (persona moral, o persona física con actividad empresarial) que se suscribe al Servicio.

1.3. **Plataforma / Servicio**: el software de gestión deportiva MYKLUBB, accesible por internet en modalidad de software como servicio (SaaS).

1.4. **Usuarios**: las personas autorizadas por EL CLIENTE (administradores y staff) para acceder a la Plataforma con credenciales.

1.5. **Datos del Cliente**: la información que EL CLIENTE y sus Usuarios cargan en la Plataforma, incluidos los datos personales de jugadores.

1.6. **DPA**: el Convenio de Tratamiento de Datos Personales celebrado por separado entre las partes.

1.7. **Aviso de Privacidad**: los avisos de privacidad aplicables publicados por EL PROVEEDOR y por EL CLIENTE.

## 2. Objeto y licencia de uso

2.1. EL PROVEEDOR otorga a EL CLIENTE una licencia de uso **no exclusiva, intransferible, revocable y limitada** a la vigencia de la suscripción, para acceder y usar la Plataforma conforme a este Contrato.

2.2. El Servicio comprende, según el plan contratado, funciones como gestión de grupos, jugadores y staff, control de asistencia, torneos, programas de entrenamiento, cobranza de cuotas (club→jugador), y gestión de uniformes e inventario.

2.3. La licencia no transfiere ningún derecho de propiedad sobre el software. Queda prohibido copiar, descompilar, realizar ingeniería inversa, revender o sublicenciar la Plataforma salvo autorización expresa.

## 3. Planes, suscripción y aceptación electrónica

3.1. El Servicio se contrata mediante planes de suscripción (por ejemplo, Starter, Club y Pro), con límites por plan (número de jugadores, grupos, staff, deportes y módulos habilitados).

3.2. El alta se realiza en línea. La **aceptación electrónica (click-through)** de este Contrato al darse de alta constituye el consentimiento de EL CLIENTE y perfecciona el acuerdo, registrándose fecha, versión y usuario que acepta.

## 4. Precios, facturación y renovación

4.1. Los precios corresponden al plan y periodicidad (mensual o anual) elegidos, y se expresan en pesos mexicanos, **más el IVA** y demás impuestos aplicables.

4.2. El pago se realiza por adelantado según la periodicidad contratada, mediante los medios habilitados en la Plataforma.

4.3. **Renovación automática**: salvo cancelación previa a la fecha de renovación, la suscripción se renueva automáticamente por periodos iguales, a la tarifa vigente.

4.4. **Cambios de plan**: EL CLIENTE puede solicitar upgrade o downgrade. El upgrade puede aplicar de inmediato con ajuste proporcional; el downgrade surte efecto en el siguiente periodo y puede implicar la pérdida de acceso a módulos o capacidad que excedan el nuevo plan.

4.5. EL PROVEEDOR puede modificar los precios notificando con anticipación razonable; los nuevos precios aplican a partir de la siguiente renovación.

## 5. Suspensión

5.1. EL PROVEEDOR podrá **suspender** el acceso al Servicio en caso de falta de pago o de incumplimiento de este Contrato, previo aviso cuando sea razonablemente posible.

5.2. Durante la suspensión los Datos del Cliente se conservan por un periodo prudente; la falta de regularización podrá derivar en la terminación y en la supresión de datos conforme al DPA.

## 6. Obligaciones del CLIENTE

6.1. Usar el Servicio de forma **lícita** y conforme a este Contrato.

6.2. Garantizar la **veracidad y licitud** de los Datos del Cliente que carga, y contar con base legítima para tratarlos.

6.3. Como **RESPONSABLE** de los datos personales de sus jugadores, obtener los consentimientos de los titulares y, tratándose de menores, de los padres o tutores, **utilizando el mecanismo de consentimiento que provee la Plataforma**, sin sustituirlo ni omitirlo.

6.4. Resguardar las **credenciales** de acceso y ser responsable de las cuentas y actos de su staff.

6.5. Poner a disposición de los titulares el Aviso de Privacidad correspondiente y atender las solicitudes de derechos ARCO que le correspondan como responsable.

## 7. Obligaciones y alcance del PROVEEDOR

7.1. Prestar el Servicio con **esfuerzos razonables**, y proveer soporte y mantenimiento conforme a la naturaleza del producto.

7.2. EL CLIENTE reconoce el **carácter evolutivo** del software: las funciones pueden actualizarse, mejorarse o modificarse con el tiempo.

## 8. Disponibilidad y mantenimiento

8.1. El Servicio se presta **"en el estado en que se encuentra" (as is)** y bajo un esquema de mejores esfuerzos, sin un acuerdo de nivel de servicio (SLA) estricto salvo pacto expreso por separado.

8.2. EL PROVEEDOR podrá realizar **ventanas de mantenimiento** que impliquen interrupciones temporales, procurando avisarlas con anticipación cuando sea posible.

## 9. Propiedad intelectual

9.1. EL PROVEEDOR conserva **todos los derechos** de propiedad intelectual sobre la Plataforma, su código, diseño y la marca MYKLUBB.

9.2. EL CLIENTE conserva la **titularidad de sus Datos y contenidos**. Otorga a EL PROVEEDOR una licencia limitada para tratarlos con el único fin de prestar el Servicio, conforme al DPA.

## 10. Datos personales y privacidad

10.1. El tratamiento de datos personales se rige por el **DPA** y por el **Aviso de Privacidad**, que se tienen por reproducidos. En caso de duda, prevalece el DPA en lo relativo a datos personales.

10.2. Para efectos de datos personales, **EL CLIENTE es RESPONSABLE y EL PROVEEDOR es ENCARGADO**, conforme a la LFPDPPP. La infraestructura y los sub-encargados se detallan en el DPA.

## 11. Confidencialidad

11.1. Cada parte se obliga a mantener la **confidencialidad** de la información no pública de la otra a la que tenga acceso con motivo de este Contrato, y a usarla solo para los fines del mismo, salvo obligación legal de divulgación.

## 12. Garantías y su exclusión

12.1. En la medida permitida por la ley, el Servicio se provee **sin garantías** implícitas de comerciabilidad, idoneidad para un fin particular o disponibilidad ininterrumpida.

## 13. Limitación de responsabilidad

13.1. En la medida permitida por la ley, la responsabilidad total de EL PROVEEDOR derivada de este Contrato **no excederá el monto pagado** por EL CLIENTE en los **12 (doce) meses** anteriores al hecho que origine la reclamación.

13.2. EL PROVEEDOR no será responsable de **daños indirectos, incidentales o consecuenciales**, ni de lucro cesante o pérdida de datos no imputable a su dolo o negligencia grave.

13.3. Nada en este Contrato limita responsabilidades que, conforme a la ley, no puedan limitarse.

## 14. Indemnización

14.1. Cada parte se obliga a **sacar en paz y a salvo** a la otra frente a reclamaciones de terceros derivadas del incumplimiento de sus obligaciones bajo este Contrato, incluida, respecto de EL CLIENTE, la falta de consentimientos válidos de los titulares.

## 15. Caso fortuito y fuerza mayor

15.1. Ninguna parte será responsable por incumplimientos debidos a causas fuera de su control razonable (caso fortuito o fuerza mayor), mientras subsistan tales causas.

## 16. Modificaciones

16.1. EL PROVEEDOR podrá modificar el Servicio y estos Términos, **notificando** a EL CLIENTE por la Plataforma o los medios de contacto registrados. El uso continuado tras la entrada en vigor implica aceptación; si EL CLIENTE no está conforme, podrá cancelar conforme a la cláusula 18.

## 17. Cesión

17.1. EL CLIENTE **no podrá ceder** este Contrato sin consentimiento previo y por escrito de EL PROVEEDOR. EL PROVEEDOR podrá cederlo en caso de reestructura, fusión o transmisión de activos, notificando a EL CLIENTE.

## 18. Vigencia, terminación y efectos

18.1. Este Contrato rige mientras la suscripción esté vigente.

18.2. Cualquiera de las partes puede **terminar** conforme a los plazos de cancelación aplicables. EL PROVEEDOR puede terminar por incumplimiento no subsanado.

18.3. Al terminar, cesa el acceso al Servicio y EL PROVEEDOR **devuelve o suprime** los Datos del Cliente conforme al DPA y a la política de retención, salvo obligación legal de conservarlos.

## 19. Ley aplicable y jurisdicción

19.1. Este Contrato se rige por las **leyes de los Estados Unidos Mexicanos**. Para su interpretación y cumplimiento, las partes se someten a los tribunales competentes de [CIUDAD/ESTADO PARA JURISDICCIÓN], **renunciando a cualquier otro fuero** que pudiera corresponderles.

## 20. Aceptación electrónica

20.1. La **aceptación electrónica (click-through)** de EL CLIENTE al darse de alta constituye su consentimiento expreso a este Contrato. Se registra fecha, versión del documento y usuario que acepta, como constancia.

## 21. Datos de las partes y aceptación

**EL PROVEEDOR:** [RAZÓN SOCIAL DE MYKLUBB] — [DOMICILIO][RFC PARTES] — [CORREO DE CONTACTO]

**EL CLIENTE:** *(datos capturados al alta: denominación, RFC, domicilio, correo y usuario que acepta)*

Aceptado electrónicamente en la fecha de alta registrada por la Plataforma.

---

**Versión / Fecha:** [FECHA/VERSIÓN]
`

// 1.0 de la declaracion que acepta, a nombre del club, el contacto de un club sin MYKLUBB al
// abrir el enlace de captura (spec 2026-10-08 §6.3). La Edge Function club-administrado guarda
// su version (VERSION_DECLARACION) en org_legal_acceptances.
const DECLARACION_CLUB_SIN_MYKLUBB_1_0 = `# Declaración del club — [NOMBRE DEL CLUB]

**Torneo:** [TORNEO]

Represento a **[NOMBRE DEL CLUB]** o tengo su autorización para registrar a sus jugadores en **[TORNEO]**.

Cuento con la autorización de las madres, padres o tutores de cada jugador menor de edad para registrar sus datos y su fotografía en este torneo, en los términos del Convenio de Tratamiento de Datos.

Sé que la organizadora recibirá su nombre, número, categoría, elegibilidad, CURP y fotografía, y que los datos se borran al terminar el torneo.

**Versión / Fecha:** [FECHA/VERSIÓN]
`

// ---------------------------------------------------------------------------------------
// 1.1 (pieza 2b, spec 2026-10-11): lo que se comparte con la organizadora de un torneo es
// fijo y se declara siempre, sin clausulas condicionales. Los revisa Macrolex antes de PROD.
// ---------------------------------------------------------------------------------------

const AVISO_CLUB_1_1 = `# Aviso de Privacidad Integral — [NOMBRE DEL CLUB]

## 1. Identidad y domicilio del responsable

[RAZÓN SOCIAL] ("el Club"), con domicilio en [DOMICILIO], es el responsable del tratamiento de los datos personales de los jugadores que inscribe, conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP).

El Club utiliza la plataforma **MYKLUBB** como encargado del tratamiento. MYKLUBB trata los datos únicamente por cuenta y bajo instrucciones del Club.

## 2. Datos personales que se recaban

**Identificación del jugador:** nombre completo, fecha de nacimiento, género, CURP, apodo o dorsal.

**Contacto de emergencia:** nombre y teléfono del contacto o tutor.

**Responsable de pago:** se captura únicamente cuando difiere del tutor; registro de cuotas y pagos.

**Teléfono del jugador:** solo para jugadores mayores de edad; es de uso interno y no se comparte con terceros.

**Documentos:** fotografía del jugador, carta responsiva y reglamento firmado.

> El Club aplica el principio de minimización: recaba únicamente los datos necesarios para la actividad deportiva. No se recaban datos de salud en la plataforma.

## 3. Finalidades del tratamiento

**Primarias** (necesarias): inscripción y gestión de la membresía; control de asistencia y conformación de equipos; participación en entrenamientos, partidos, ligas y torneos, incluida la verificación de la identidad y la elegibilidad del jugador ante la organizadora de cada torneo o liga en que el Club lo inscriba; gestión de cuotas y pagos; cumplimiento de reglamentos aplicables.

**Secundarias** (el titular puede oponerse): comunicaciones informativas del Club y, en su caso, uso de imagen conforme a la sección 5.

> Para oponerse a finalidades secundarias, enviar un correo a [CORREO ARCO]. Negarse no afecta la membresía del jugador.

## 4. Tratamiento de datos de menores de edad

La mayoría de los jugadores son menores. En todo tratamiento prevalece el **interés superior de la niña, niño y adolescente**. El registro y la autorización los otorga el padre, madre o tutor legal, quien declara contar con la representación legal del menor. Cuando el jugador es **mayor de edad, otorga el consentimiento por sí mismo**.

## 5. Uso de imagen (fotografía)

La fotografía se utiliza para identificar al jugador dentro del Club y la plataforma y, cuando el Club lo inscribe en un torneo o liga, para que la organizadora y el club rival de cada partido verifiquen su identidad (sección 6). Cualquier uso para difusión (redes sociales, materiales promocionales) requiere **autorización expresa y por separado** del tutor (o del jugador mayor de edad), revocable en cualquier momento.

## 6. Transferencias, remisiones y almacenamiento

**Encargado (plataforma):** MYKLUBB trata los datos por cuenta del Club. No los usa para fines propios; su personal de soporte accede solo cuando es necesario para resolver incidencias, dejando registro de cada acceso.

**Almacenamiento en la nube (sub-encargado):** la información se almacena mediante Supabase, que puede alojar los datos en servidores **fuera de México**. Los datos se mantienen cifrados en reposo y en tránsito, bajo obligaciones contractuales de confidencialidad y seguridad.

**Identidad compartida entre clubes:** cuando un jugador pertenece a más de un club, ciertos datos de identidad (CURP) pueden mantenerse de forma unificada para evitar duplicidad. Cada club accede únicamente a los datos de los jugadores que él inscribe.

**Organizadores de torneos y ligas:** cuando el Club inscribe al jugador en un torneo o liga, comparte con la organizadora de ese torneo o liga el **nombre, número, categoría, fecha de nacimiento, CURP y fotografía** del jugador, y el **nombre y la fotografía** de las personas del cuerpo técnico que acompañan al equipo. La organizadora los recibe con la única finalidad de **verificar la identidad del jugador y su elegibilidad** (que juegue en la categoría que le corresponde y en un solo equipo a la vez) y **no puede usarlos para difusión** ni para otros fines. Al recibirlos, la organizadora se convierte en responsable de esos datos y responde por su tratamiento. Los demás clubes que participan en el torneo ven el **nombre y el número** del jugador. El club contra el que juega cada partido ve además su **fotografía**, solo desde dos días antes del partido y hasta el final de ese día, únicamente para verificar su identidad en ese partido; no puede guardarla, compartirla ni usarla para difusión. **No se comparten datos de contacto ni de pago, ni otros documentos.**

> Salvo lo anterior y las excepciones de la LFPDPPP, no se transfieren datos a terceros sin consentimiento.

## 7. Conservación de datos

Los datos y documentos se conservan mientras dure la membresía del jugador y se eliminan o archivan una vez que el jugador se da de baja o permanece inactivo por el periodo definido por el Club.

## 8. Medios para ejercer los derechos ARCO

El titular o su representante legal puede ejercer en cualquier momento sus derechos de **Acceso, Rectificación, Cancelación y Oposición (ARCO)** y revocar el consentimiento, enviando solicitud a [CORREO ARCO] con: nombre del titular y solicitante, documento que acredite identidad y, en su caso, representación legal del menor, descripción del derecho que desea ejercer y un medio para recibir respuesta. El ejercicio es gratuito.

## 9. Cookies

La plataforma puede emplear cookies para el funcionamiento del servicio. El usuario puede configurar su navegador para limitarlas.

## 10. Cambios al aviso

Este aviso puede modificarse por cambios legales u operativos; las modificaciones se comunicarán a través de la plataforma o por los medios de contacto registrados.

## 11. Autoridad

El titular puede acudir ante la autoridad competente en materia de protección de datos personales en posesión de particulares en México.

---

**Última actualización:** [FECHA]
`

const DPA_1_1 = `# Convenio de Tratamiento de Datos Personales (DPA) — MYKLUBB ↔ Club

> Convenio entre **MYKLUBB** (encargado) y el **Club** (responsable). Define cómo MYKLUBB trata, por cuenta del Club, los datos personales de los jugadores.

## 1. Objeto y roles

**Las partes:** [RAZÓN SOCIAL DE MYKLUBB], operador de la plataforma MYKLUBB, y el Club a cuyo nombre se acepta este convenio. Quien acepta a nombre del Club declara tener facultades para obligarlo: es el titular de la cuenta del Club en la plataforma o, si el Club no usa la plataforma, la persona que lo representa ante el torneo en que participa.

El Club es el **responsable** de los datos personales de sus jugadores. MYKLUBB es el **encargado** que los trata exclusivamente por cuenta y bajo instrucciones del Club, para prestar el servicio de gestión deportiva de la plataforma.

## 2. Obligaciones del Club (responsable)

1. **Obtener el consentimiento válido** del tutor (o del jugador mayor de edad) para el tratamiento de los datos de cada jugador, **utilizando el mecanismo que provee la plataforma**, sin sustituirlo ni omitirlo.
2. Poner a disposición de los titulares el **aviso de privacidad** del Club.
3. Garantizar que los datos que captura son exactos y que cuenta con base legítima para tratarlos.
4. Atender, como responsable, las solicitudes de derechos ARCO de sus jugadores (con la asistencia de MYKLUBB prevista en §3).

## 3. Obligaciones de MYKLUBB (encargado)

1. Tratar los datos **únicamente conforme a las instrucciones** del Club y a las finalidades del servicio; no usarlos para fines propios.
2. Guardar **confidencialidad** y exigirla a su personal.
3. Implementar **medidas de seguridad** administrativas, técnicas y físicas (cifrado en reposo y en tránsito, control de acceso, registro de accesos).
4. **Acceso de soporte limitado:** el personal de MYKLUBB accede a los datos solo cuando es necesario para resolver incidencias, dejando **registro de cada acceso**.
5. **Asistir al Club** en la atención de solicitudes ARCO y en notificaciones de incidentes de seguridad.
6. **Devolver o suprimir** los datos al terminar la relación, conforme a la política de retención.
7. No subcontratar fuera de los sub-encargados autorizados (§4) sin informar al Club.

## 4. Sub-encargados autorizados

El Club autoriza a MYKLUBB a apoyarse en los siguientes sub-encargados:

| Sub-encargado | Función | Ubicación de datos |
|---|---|---|
| Supabase | Base de datos, almacenamiento y autenticación | Fuera de México (nube; cifrado en reposo y en tránsito) |

MYKLUBB mantiene con sus sub-encargados obligaciones de confidencialidad y seguridad equivalentes a las de este convenio (p. ej. el DPA firmado con Supabase).

## 5. Transferencia internacional

Al almacenarse los datos con un sub-encargado fuera de México, existe una transferencia internacional bajo la figura de remisión a encargado. MYKLUBB se asegura de que dicho proveedor quede obligado contractualmente a proteger los datos conforme a este convenio.

## 6. Capa de identidad unificada y corresponsabilidad

MYKLUBB mantiene, **por decisión propia y para uso interno del sistema**, un registro de identidad unificado por CURP que le permite reconocer a un mismo atleta a lo largo de su trayectoria deportiva, aunque participe en distintos clubes, ligas o regiones. La finalidad es evitar registros duplicados, dar seguimiento al desarrollo deportivo del atleta en el tiempo y, eventualmente, permitir que el propio atleta (o su tutor) acceda a su historial deportivo.

Respecto de **esta capa de identidad unificada**, y solo de ella, MYKLUBB actúa como **corresponsable** (no como encargado), pues su finalidad es propia de MYKLUBB y no una instrucción del Club. En consecuencia, respecto de esa capa MYKLUBB:

1. Cuenta con **base de licitud propia**: la autorización expresa que el tutor (o el atleta mayor de edad) otorga en la carta de consentimiento.
2. Declara esta finalidad en su **propio aviso de privacidad**.
3. Responde **directamente ante la autoridad** por el tratamiento de esa capa.

La correlación de identidad es de uso interno: **ningún club accede a la información de un jugador que corresponda a otro club**, salvo que el propio atleta o su tutor lo autorice. Respecto del resto de los datos de cada jugador, se mantiene la relación responsable (Club) / encargado (MYKLUBB) del §1.

## 7. Compartición con organizadores de torneos

En un torneo o liga, su organizadora tiene que poder verificar quién juega. Por eso, cuando el Club inscribe un equipo en un torneo o liga de la plataforma, esta comparte con la organizadora, siempre y en los mismos términos para todos los clubes participantes:

1. de cada jugador del equipo: **nombre, número, categoría, fecha de nacimiento, CURP y fotografía**;
2. de cada persona del cuerpo técnico inscrita con el equipo: **nombre y fotografía**.

La organizadora recibe estos datos con la única finalidad de **verificar la identidad y la elegibilidad** de los jugadores (que cada uno juegue en la categoría que le corresponde y en un solo equipo a la vez) y **no puede usarlos para difusión** ni para otros fines. Al recibirlos, la organizadora actúa como **responsable** de ellos y responde por su tratamiento. La plataforma le muestra las fotografías mediante enlaces temporales que caducan a los pocos minutos.

Los demás clubes que participan en el torneo ven, de los jugadores del Club, el **nombre y el número**, y del cuerpo técnico, el nombre. El club contra el que se juega cada partido ve además la **fotografía** de los jugadores y del cuerpo técnico, solo desde dos días antes del partido y hasta el final de ese día, con la única finalidad de **verificar su identidad en ese partido**; no puede guardarla, compartirla ni usarla para difusión. La plataforma se la muestra mediante enlaces temporales que caducan a los pocos minutos. La fotografía de un jugador solo se le muestra si su tutor (o el jugador mayor de edad) firmó en la plataforma el Aviso que declara esta compartición; si el Club no usa la plataforma, basta la declaración de quien lo representa.

Los datos de contacto (incluidos el teléfono y el correo del cuerpo técnico), los de pago y los demás documentos son de uso interno y no se comparten.

El Aviso de Privacidad del Club que provee la plataforma declara esta compartición. El Club se obliga a contar, para cada jugador que inscriba en un torneo, con el consentimiento de su tutor (o del jugador mayor de edad) sobre ese Aviso: si usa la plataforma, lo recaba con el mecanismo que esta provee; si no la usa, quien acepta a nombre del Club declara contar con él. **Inscribir equipos en torneos de la plataforma requiere haber aceptado esta versión del convenio.**

## 8. Incidentes de seguridad

MYKLUBB notificará al Club sin demora indebida cualquier incidente de seguridad que afecte los datos de los jugadores del Club, con la información disponible para que el Club cumpla sus obligaciones como responsable.

## 9. Vigencia y terminación

Este convenio rige mientras el Club use la plataforma. Al terminar, MYKLUBB devolverá o suprimirá los datos conforme a la política de retención, salvo obligación legal de conservarlos.

## 10. Aceptación

La aceptación electrónica de quien acepta a nombre del Club (al activar o usar su cuenta en la plataforma o, si el Club no usa la plataforma, en el enlace de captura de un torneo), o la firma del documento, constituye la conformidad del Club con este convenio. Se registran la fecha, la versión, el texto mostrado y quién acepta a nombre del Club.

---

**Versión / fecha:** [FECHA/VERSIÓN]
`

const CARTA_CONSENTIMIENTO_1_1 = `# Autorización y consentimiento del padre, madre o tutor — [NOMBRE DEL CLUB]

**Responsable del tratamiento:** [NOMBRE DEL CLUB]

Este documento permite a [NOMBRE DEL CLUB] (en adelante, el Club) recabar la autorización de quien ejerce la patria potestad o tutela del menor para el tratamiento de sus datos personales, conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP) y a la Ley General de los Derechos de Niñas, Niños y Adolescentes (LGDNNA), y al Aviso de Privacidad del Club. En todo tratamiento prevalece el **interés superior** de la niña, niño o adolescente.

## Datos del jugador

Nombre completo · CURP · Fecha de nacimiento · Club y categoría.

## Datos del padre, madre o tutor

Nombre completo · Parentesco · Teléfono · Correo electrónico.

## Tratamiento de datos del menor (obligatorio)

Como padre, madre o tutor del menor identificado en este documento, manifiesto que:

1. Cuento con la **representación legal** del menor.
2. He leído y acepto el **Aviso de Privacidad del Club**.
3. **Autorizo el tratamiento** de los datos personales del menor para su inscripción, identificación, gestión deportiva, participación en entrenamientos, partidos, ligas y torneos, y administración de cuotas.
4. **Autorizo que, si el Club inscribe al menor en un torneo o liga**, comparta con la organizadora su nombre, número, categoría, fecha de nacimiento, CURP y fotografía, únicamente para verificar su identidad y su elegibilidad y nunca para difusión, como lo describe el Aviso de Privacidad del Club. Los demás clubes del torneo ven su nombre y su número, y el club contra el que juega cada partido, también su fotografía, solo desde dos días antes del partido y hasta el final de ese día, para verificar su identidad.
5. **Autorizo a la plataforma MYKLUBB** a mantener un registro de identidad unificado por CURP, de uso interno, para reconocer al atleta a lo largo de su trayectoria deportiva, evitar registros duplicados y dar seguimiento a su desarrollo. Ningún club accede a la información del menor que corresponda a otro club, salvo que yo lo autorice.

> Acepto y autorizo el tratamiento de los datos del menor conforme a lo anterior.

## Uso de imagen para difusión (opcional)

La fotografía del menor se utiliza para su identificación dentro del Club y la plataforma y, en los torneos en que participe, ante la organizadora y ante el club rival de cada partido. De forma adicional, el Club solicita su autorización para usar la imagen del menor en **redes sociales y materiales de difusión**. Esta autorización es **revocable en cualquier momento**.

**Negar esta autorización no afecta la inscripción del menor.**

---

Firma del padre, madre o tutor · Fecha.

*Aviso de Privacidad completo disponible en la propia plataforma.*
`

// La acepta, a nombre del club, el contacto de un club sin MYKLUBB al abrir el enlace de
// captura. 1.1: agrega la fecha de nacimiento y la foto del cuerpo tecnico, y precisa el
// borrado (contrato B10) para que el resumen del enlace no diga mas que el texto legal.
const DECLARACION_CLUB_SIN_MYKLUBB_1_1 = `# Declaración del club — [NOMBRE DEL CLUB]

**Torneo:** [TORNEO]

Represento a **[NOMBRE DEL CLUB]** o tengo su autorización para registrar a sus jugadores y a su cuerpo técnico en **[TORNEO]**, y acepto a su nombre el Convenio de Tratamiento de Datos.

Cuento con la autorización de las madres, padres o tutores de cada jugador menor de edad (o del propio jugador, si es mayor de edad) para registrar sus datos y su fotografía en este torneo, y con la de cada persona del cuerpo técnico para registrar su nombre y su fotografía.

Sé que la organizadora recibirá, de cada jugador, su nombre, número, categoría, fecha de nacimiento, CURP y fotografía, y del cuerpo técnico, su nombre y su fotografía, solo para verificar la identidad y la elegibilidad de los jugadores. Los demás clubes del torneo verán el nombre y el número de los jugadores; el club rival de cada partido verá además la fotografía de los jugadores y del cuerpo técnico, solo desde dos días antes del partido y hasta el final de ese día, para verificar su identidad. El teléfono y el correo del cuerpo técnico no se comparten.

Los datos se borran al terminar el torneo: cuando la organizadora lo archiva o, a más tardar, 30 días después.

**Versión / Fecha:** [FECHA/VERSIÓN]
`

// Registro versionado. `vigente: true` = version que se muestra y se acepta hoy.
export const LEGAL_DOCUMENTS = [
  {
    docKey: LEGAL_DOC_KEYS.AVISO_CLUB,
    // 1.0: la CURP y la foto eran clausulas CONDICIONALES ([TRANSFERENCIA CURP] y
    // [TRANSFERENCIA FOTO]) segun las casillas de comparticion de cada club. Por eso dos
    // clubes en "1.0" pueden haber leido textos distintos, y cada firma guarda ADEMAS la copia
    // del documento tal como se mostro: la version sola no reproduce lo que se leyo.
    version: '1.0',
    titulo: 'Aviso de Privacidad del Club',
    contenido: AVISO_CLUB_1_0,
    vigente: false,
  },
  {
    docKey: LEGAL_DOC_KEYS.AVISO_CLUB,
    // 1.1: lo que se comparte con la organizadora de un torneo es fijo y se declara siempre.
    // Una firma digital sobre este texto cubre torneos (consentimiento-otp, firma.ts).
    version: '1.1',
    titulo: 'Aviso de Privacidad del Club',
    contenido: AVISO_CLUB_1_1,
    vigente: true,
  },
  {
    docKey: LEGAL_DOC_KEYS.AVISO_MYKLUBB,
    version: '1.0',
    titulo: 'Aviso de Privacidad de MYKLUBB',
    contenido: AVISO_MYKLUBB,
    vigente: true,
  },
  {
    docKey: LEGAL_DOC_KEYS.DPA,
    // 1.0: la §7 tenia las clausulas condicionales [TRANSFERENCIA CURP DPA] y
    // [TRANSFERENCIA FOTO DPA].
    version: '1.0',
    titulo: 'Convenio de Tratamiento de Datos (DPA)',
    contenido: DPA_1_0,
    vigente: false,
  },
  {
    docKey: LEGAL_DOC_KEYS.DPA,
    // 1.1: la §7 es obligatoria y fija; la base exige DPA >= 1.1 para inscribir en torneos de
    // la red (_condiciones_torneo_aceptadas, migracion 20261012_datos_del_torneo.sql).
    version: '1.1',
    titulo: 'Convenio de Tratamiento de Datos (DPA)',
    contenido: DPA_1_1,
    vigente: true,
  },
  {
    docKey: LEGAL_DOC_KEYS.CARTA_CONSENTIMIENTO,
    version: '1.0',
    titulo: 'Carta de consentimiento del tutor',
    contenido: CARTA_CONSENTIMIENTO_1_0,
    vigente: false,
  },
  {
    docKey: LEGAL_DOC_KEYS.CARTA_CONSENTIMIENTO,
    // 1.1: agrega la autorizacion de lo que se comparte en torneos (punto 4). Una firma en
    // papel registrada con esta carta cubre torneos (registrar_consentimiento_fisico).
    version: '1.1',
    titulo: 'Carta de consentimiento del tutor',
    contenido: CARTA_CONSENTIMIENTO_1_1,
    vigente: true,
  },
  {
    docKey: LEGAL_DOC_KEYS.TOS_SAAS,
    version: '1.0',
    titulo: 'Términos del Servicio (Contrato SaaS)',
    contenido: TOS_SAAS,
    vigente: true,
  },
  {
    docKey: LEGAL_DOC_KEYS.DECLARACION_CLUB_SIN_MYKLUBB,
    version: '1.0',
    titulo: 'Declaración del club sin MYKLUBB',
    contenido: DECLARACION_CLUB_SIN_MYKLUBB_1_0,
    vigente: false,
  },
  {
    docKey: LEGAL_DOC_KEYS.DECLARACION_CLUB_SIN_MYKLUBB,
    version: '1.1',
    titulo: 'Declaración del club sin MYKLUBB',
    contenido: DECLARACION_CLUB_SIN_MYKLUBB_1_1,
    vigente: true,
  },
]

export const getLegalDocDefinition = (docKey) =>
  LEGAL_DOCUMENTS.find((d) => d.docKey === docKey && d.vigente) || null
